// Adapters mapping the production API's camelCase shapes to the UI's shape.

export function mapProfile(u) {
  if (!u) return null;
  const photoList = Array.isArray(u.photos) && u.photos.length
    ? u.photos
    : (u.photoBase64 ? [u.photoBase64] : []);
  const photos = photoList.map((src, i) => ({ id: String(i), src, is_primary: i === 0 }));
  const interests = u.interests || [];
  const name = (u.displayName || [u.firstName, u.lastName].filter(Boolean).join(" ") || "Member").trim();
  return {
    id: u.id,
    name,
    firstName: u.firstName,
    lastName: u.lastName,
    age: u.age,
    height_cm: u.height,
    heightUnit: u.heightUnit || "metric",
    weight: u.weight,
    weightUnit: u.weightUnit || "kg",
    eyeColor: u.eyeColor,
    gender: u.gender,
    city: (u.city || "").trim(),
    country: (u.country || "").trim(),
    state: u.state,
    ethnicity: u.ethnicity,
    showCountry: u.showCountry !== false,
    showState: u.showState !== false,
    showCity: u.showCity !== false,
    showEthnicity: u.showEthnicity !== false,
    showLanguages: u.showLanguages !== false,
    sect: u.sect,
    prayer_habit: u.prayerFrequency,
    religiosity: u.religiosity,
    wantSpouse: u.wantSpouse,
    marital_status: u.maritalStatus,
    children: u.children,
    wantChildren: u.wantChildren,
    waliInvolvement: u.waliInvolvement,
    education: u.educationLevel,
    profession: (u.profession || "").trim(),
    bio: u.bio,
    languages: u.languages || [],
    hobbies: u.hobbies || [],
    interests,
    personality: u.personality,
    prefAgeMin: u.prefAgeMin ?? u.preferredAgeMin,
    prefAgeMax: u.prefAgeMax ?? u.preferredAgeMax,
    relocation: u.relocation && u.relocation !== "No",
    relocateChoice: u.relocation,
    nationality: u.nationality,
    verified_wali: u.waliInvolvement === "Yes" || !!u.waliName,
    verified: !!u.verified,
    is_premium: !!u.premium,
    role: u.role,
    accountType: u.accountType,
    assistantRole: u.assistantRole,
    organization: u.organization || u.affiliation || u.mosque || "",
    hidden: !!(u.profileHidden ?? u.hidden),
    photo: photos[0]?.src || null,
    photos,
    wali: { name: u.waliName, relation: u.waliRelationship, phone: u.waliPhone, email: u.waliEmail, preferredContact: u.waliPreferredContact, notes: u.waliNotes },
    shareWali: !!u.shareWali,
    contact: {
      email: u.contactEmail,
      phone: u.contactPhone,
      whatsapp: u.contactWhatsapp,
      socials: u.socials || {},
    },
    contactPhone: u.contactPhone || "",
    contactWhatsapp: u.contactWhatsapp || "",
    facebook: u.socials?.facebook || "",
    instagram: u.socials?.instagram || "",
    shareTypes: u.shareTypes || [],
    shareEmail: (u.shareTypes || []).includes("email"),
    sharePhone: (u.shareTypes || []).includes("phone"),
    shareWhatsapp: (u.shareTypes || []).includes("whatsapp"),
    shareFacebook: (u.shareTypes || []).includes("facebook"),
    shareInstagram: (u.shareTypes || []).includes("instagram"),
    match_percent: u.matchScore ?? u.compatibility ?? u.matchPercent ?? u.compatibilityScore ?? undefined,
    near: !!u.near,
    near_reason: u.nearReason,
    raw: u,
  };
}

export function mapUser(u) {
  if (!u) return null;
  const source = u.premiumSource || u.subscription?.source || (u.compPremium?.active ? "complimentary" : null);
  return {
    id: u.id,
    email: u.email,
    role: u.role,
    account_type: u.accountType || "member",
    assistant_role: u.assistantRole || null,
    is_assistant: u.accountType === "assistant",
    verified: !!u.verified,
    email_verified: !!(u.verifications?.email ?? u.verified),
    verifications: u.verifications || {},
    is_reviewer: (u.email || "").includes("review") || (u.email || "").includes(".subs@"),
    is_premium: !!u.premium,
    hidden: !!(u.profileHidden ?? u.hidden),
    profile_complete: !!(u.displayName && u.city && u.sect),
    profile: mapProfile(u),
    subscription: {
      status: u.premium ? "active" : "none",
      source,
      plan: u.premiumPlan,
      current_period_end: u.premiumExpiresAt,
      reason: u.compPremium?.reason,
      lifetime: u.compPremium?.lifetime,
    },
    notification_prefs: u.notificationPrefs || { interest: true, match: true, marketing: false },
    raw: u,
  };
}

// Map UI profile-edit fields back to the production camelCase body.
export function formatHeight(cm, unit) {
  if (!cm) return null;
  if (unit === "imperial") {
    const totalIn = Math.round(cm / 2.54);
    const ft = Math.floor(totalIn / 12);
    const inch = totalIn % 12;
    return `${ft}'${inch}"`;
  }
  return `${cm} cm`;
}

export function formatWeight(w, unit) {
  if (!w) return null;
  const u = (unit || "kg").toLowerCase();
  return u.includes("lb") || u.includes("imp") ? `${w} lb` : `${w} kg`;
}

export function toProdProfile(p) {
  const body = {};
  if (p.name !== undefined) body.displayName = p.name;
  if (p.firstName !== undefined) body.firstName = p.firstName;
  if (p.lastName !== undefined) body.lastName = p.lastName;
  if (p.gender !== undefined) body.gender = p.gender;
  if (p.age !== undefined) body.age = p.age;
  if (p.bio !== undefined) body.bio = p.bio;
  if (p.country !== undefined) body.country = p.country;
  if (p.state !== undefined) body.state = p.state;
  if (p.city !== undefined) body.city = p.city;
  if (p.ethnicity !== undefined) body.ethnicity = p.ethnicity;
  if (p.showCountry !== undefined) body.showCountry = p.showCountry;
  if (p.showState !== undefined) body.showState = p.showState;
  if (p.showCity !== undefined) body.showCity = p.showCity;
  if (p.showEthnicity !== undefined) body.showEthnicity = p.showEthnicity;
  if (p.showLanguages !== undefined) body.showLanguages = p.showLanguages;
  if (p.hidden !== undefined) body.profileHidden = p.hidden;
  if (p.height_cm !== undefined) body.height = p.height_cm;
  if (p.heightUnit !== undefined) body.heightUnit = p.heightUnit;
  if (p.weight !== undefined) body.weight = p.weight;
  if (p.weightUnit !== undefined) body.weightUnit = p.weightUnit;
  if (p.eyeColor !== undefined) body.eyeColor = p.eyeColor;
  if (p.sect !== undefined) body.sect = p.sect;
  if (p.prayer_habit !== undefined) body.prayerFrequency = p.prayer_habit;
  if (p.religiosity !== undefined) body.religiosity = p.religiosity;
  if (p.wantSpouse !== undefined) body.wantSpouse = p.wantSpouse;
  if (p.marital_status !== undefined) body.maritalStatus = p.marital_status;
  if (p.children !== undefined) body.children = p.children;
  if (p.wantChildren !== undefined) body.wantChildren = p.wantChildren;
  if (p.waliInvolvement !== undefined) body.waliInvolvement = p.waliInvolvement;
  if (p.relocateChoice !== undefined) body.relocation = p.relocateChoice;
  else if (p.relocation !== undefined) body.relocation = p.relocation ? "Open" : "No";
  if (p.education !== undefined) body.educationLevel = p.education;
  if (p.profession !== undefined) body.profession = p.profession;
  if (p.organization !== undefined) body.organization = p.organization;
  if (p.languages !== undefined) body.languages = p.languages;
  if (p.hobbies !== undefined) body.hobbies = p.hobbies;
  if (p.interests !== undefined) body.interests = p.interests;
  if (p.personality !== undefined) body.personality = p.personality;
  if (p.prefAgeMin !== undefined) body.prefAgeMin = p.prefAgeMin;
  if (p.prefAgeMax !== undefined) body.prefAgeMax = p.prefAgeMax;
  if (p.photoBase64 !== undefined) body.photoBase64 = p.photoBase64;
  if (p.contactPhone !== undefined) body.contactPhone = p.contactPhone;
  if (p.contactWhatsapp !== undefined) body.contactWhatsapp = p.contactWhatsapp;
  if (p.facebook !== undefined || p.instagram !== undefined) {
    body.socials = { facebook: p.facebook || "", instagram: p.instagram || "" };
  }
  if (p.shareEmail !== undefined || p.sharePhone !== undefined || p.shareWhatsapp !== undefined || p.shareFacebook !== undefined || p.shareInstagram !== undefined) {
    const st = [];
    if (p.shareEmail) st.push("email");
    if (p.sharePhone) st.push("phone");
    if (p.shareWhatsapp) st.push("whatsapp");
    if (p.shareFacebook) st.push("facebook");
    if (p.shareInstagram) st.push("instagram");
    body.shareTypes = st;
  }
  if (p.shareWali !== undefined) body.shareWali = p.shareWali;
  if (p.wali) {
    body.waliName = p.wali.name || "";
    body.waliRelationship = p.wali.relation || "";
    body.waliPhone = p.wali.phone || "";
    body.waliEmail = p.wali.email || "";
    body.waliPreferredContact = p.wali.preferredContact || "Either";
    body.waliNotes = p.wali.notes || "";
  }
  return body;
}

export const billingSourceLabel = (source) => {
  switch (source) {
    case "stripe": return "Web (Stripe)";
    case "google_play": return "Google Play";
    case "apple": return "Apple App Store";
    case "complimentary": return "Complimentary (granted by Nikah Match)";
    default: return source || "—";
  }
};
